import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Activity, AlertTriangle, CheckCircle2, Loader2, PowerOff, RefreshCw, Search, BellRing, Check } from 'lucide-react';
import { formatDateTimeBR } from '../lib/dateFormat';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_ROBO = {
  ok: { label: 'Robô funcionando', cls: 'border-emerald-700 bg-emerald-950/40 text-emerald-300', Icon: CheckCircle2 },
  parado: { label: 'Robô PARADO', cls: 'border-red-600 bg-red-950/50 text-red-300', Icon: AlertTriangle },
  desligado: { label: 'Robô desligado', cls: 'border-zinc-700 bg-zinc-900 text-zinc-400', Icon: PowerOff },
};

const STATUS_JOB = {
  ok: { label: 'concluído', cls: 'text-emerald-400' },
  rodando: { label: 'rodando', cls: 'text-cyan-400' },
  erro: { label: 'falhou', cls: 'text-red-400' },
  perdido: { label: 'não rodou', cls: 'text-red-400' },
  na_fila: { label: 'na fila', cls: 'text-yellow-400' },
  aguardando: { label: 'aguardando horário', cls: 'text-zinc-400' },
  desligado: { label: 'desligado', cls: 'text-zinc-500' },
};

function JobHoje({ job }) {
  const st = STATUS_JOB[job.status] || STATUS_JOB.aguardando;
  const extra = job.tipo === 'bloq' ? job.bloqueadas : job.enviados;
  return (
    <div className="bg-zinc-950/60 border border-zinc-800 rounded-md px-3 py-2 min-w-[150px]" data-testid={`job-hoje-${job.tipo}`}>
      <div className="text-[11px] text-zinc-500">{job.nome} · {job.hora}h</div>
      <div className={`text-sm font-semibold ${st.cls}`}>{st.label}</div>
      {job.status === 'ok' && extra != null && (
        <div className="text-[11px] text-zinc-400">{extra} {job.tipo === 'bloq' ? 'bloqueada(s)' : 'enviado(s)'}{job.erros ? ` · ${job.erros} erro(s)` : ''}</div>
      )}
      {job.erro && <div className="text-[11px] text-red-400 truncate" title={job.erro}>{job.erro}</div>}
    </div>
  );
}

function AlertaItem({ alerta, onLido }) {
  const critico = alerta.nivel === 'critico';
  return (
    <div className={`rounded-md border px-3 py-2 text-xs flex gap-3 ${critico ? 'border-red-800 bg-red-950/30' : 'border-amber-800 bg-amber-950/20'} ${alerta.lido ? 'opacity-50' : ''}`}
      data-testid={`alerta-${alerta.id}`}>
      <BellRing className={`w-4 h-4 shrink-0 mt-0.5 ${critico ? 'text-red-400' : 'text-amber-400'}`} />
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-zinc-200">{alerta.titulo}</div>
        {alerta.detalhe && <div className="text-zinc-400 whitespace-pre-line mt-0.5 max-h-24 overflow-y-auto">{alerta.detalhe}</div>}
        <div className="text-zinc-500 mt-1">{formatDateTimeBR(alerta.criado_em, '—')}{alerta.whatsapp ? ` · WhatsApp: ${alerta.whatsapp}` : ''}</div>
      </div>
      {!alerta.lido && (
        <button onClick={() => onLido(alerta.id)} className="text-zinc-400 hover:text-emerald-400 transition-colors self-start" title="Marcar como lido" data-testid={`btn-alerta-lido-${alerta.id}`}>
          <Check className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

export const RoboSaudeCard = () => {
  const [saude, setSaude] = useState(null);
  const [corrigindo, setCorrigindo] = useState(false);

  const carregar = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/automacao/bloqueio/saude`, { withCredentials: true });
      setSaude(data);
    } catch (e) {
      setSaude({ status: 'parado', erro_carregar: e.response?.data?.detail || e.message, jobs_hoje: [], alertas: [] });
    }
  }, []);

  useEffect(() => {
    carregar();
    const t = setInterval(carregar, 60000);
    return () => clearInterval(t);
  }, [carregar]);

  const marcarLido = async (id) => {
    await axios.post(`${API_URL}/api/automacao/bloqueio/alertas/${encodeURIComponent(id)}/lido`, {}, { withCredentials: true });
    carregar();
  };

  const corrigirExpiracoes = async () => {
    if (!window.confirm('Buscar na Tip a data de expiração das linhas que estão sem data? (Datas já preenchidas não são alteradas)')) return;
    setCorrigindo(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/automacao/bloqueio/corrigir-expiracoes-vazias`, {}, { withCredentials: true });
      toast.success(`${data.preenchidas} de ${data.verificadas} linha(s) corrigida(s). ${data.sem_data?.length || 0} continuam sem data.`);
      carregar();
    } catch (e) {
      toast.error('Erro: ' + (e.response?.data?.detail || e.message));
    }
    setCorrigindo(false);
  };

  if (!saude) {
    return <div className="h-24 rounded-lg border border-zinc-800 bg-zinc-950 animate-pulse" data-testid="robo-saude-loading" />;
  }

  const cfg = STATUS_ROBO[saude.status] || STATUS_ROBO.parado;
  const alertasVisiveis = (saude.alertas || []).filter(a => !a.lido).slice(0, 5);

  return (
    <div className={`rounded-lg border p-4 space-y-3 ${cfg.cls}`} data-testid="robo-saude-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <cfg.Icon className="w-6 h-6" />
          <div>
            <div className="font-bold text-base" data-testid="robo-status">{cfg.label}</div>
            <div className="text-xs text-zinc-400" data-testid="robo-ultimo-sinal">
              {saude.erro_carregar
                ? `Não foi possível consultar o robô: ${saude.erro_carregar}`
                : saude.minutos_sem_sinal == null
                  ? 'Ainda sem sinal de vida (aguarde 1-2 min após reiniciar o servidor)'
                  : `Última verificação há ${Math.round(saude.minutos_sem_sinal)} min · verifica a cada 5 min`}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {saude.alertas_nao_lidos > 0 && (
            <span className="text-xs font-semibold bg-red-600 text-white rounded-full px-2 py-0.5" data-testid="robo-alertas-count">
              {saude.alertas_nao_lidos} alerta(s)
            </span>
          )}
          <Button onClick={carregar} variant="outline" size="sm" className="border-zinc-700 bg-transparent" data-testid="btn-atualizar-saude">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {saude.jobs_hoje?.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <div className="flex items-center gap-1 text-xs text-zinc-400 mr-1"><Activity className="w-3.5 h-3.5" /> Hoje:</div>
          {saude.jobs_hoje.map(j => <JobHoje key={j.tipo} job={j} />)}
        </div>
      )}

      {saude.linhas_sem_expiracao > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs bg-zinc-950/60 border border-amber-800/60 rounded-md px-3 py-2" data-testid="robo-sem-expiracao">
          <span className="text-amber-300">
            {saude.linhas_sem_expiracao} linha(s) ativa(s) sem data de expiração — o robô não consegue bloqueá-las.
          </span>
          <Button onClick={corrigirExpiracoes} disabled={corrigindo} size="sm" className="bg-amber-700 hover:bg-amber-600 text-white h-7" data-testid="btn-corrigir-expiracoes">
            {corrigindo ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Search className="w-3.5 h-3.5 mr-1" />}
            Buscar datas na Tip
          </Button>
        </div>
      )}

      {saude.ativo && !saude.telefone_alerta_configurado && (
        <div className="text-xs text-zinc-400" data-testid="robo-sem-telefone">
          Dica: cadastre um WhatsApp para alertas em "Configurações & Ferramentas" para ser avisado se o robô parar.
        </div>
      )}

      {alertasVisiveis.length > 0 && (
        <div className="space-y-1.5" data-testid="robo-alertas-lista">
          {alertasVisiveis.map(a => <AlertaItem key={a.id} alerta={a} onLido={marcarLido} />)}
        </div>
      )}
    </div>
  );
};
